import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './EngagementManagement.scss';
import { type ColumnDef } from '@tanstack/react-table';
import useLocale from '../locale/useLocale';
import useTitle from '../helpers/useTitle';
import { Search } from '@appkit4/react-components/search';
import { Button } from '@appkit4/react-components/button';
import { Loading } from '@appkit4/react-components/loading';
import {
    useGetUserInfo,
    useGetAvailableTenants,
    useCreateTenant,
    useUpdateTenant,
    useDeleteTenant,
    useActivateTenant,
    getGetAvailableTenantsQueryKey,
} from '../api/generated/endpoint';
import DataTable from '../components/reusables/DataTable';
import EngagementTableIcons from '../components/EngagementTableIcons';
import Pagination from '../components/reusables/Pagination';
import Modal from '../components/reusables/Modal';
import EngagementForm, {
    EngagementFormData,
} from '../components/EngagementForm';
import DropDown from '../components/reusables/DropDown';
import LoadingOverlay from '../components/reusables/LoadingOverlay';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTenant } from '../context/TenantContext';
import useGenericErrorToast from '../helpers/useGenericErrorToast';
import { AppRoutes } from '../Constants';

export const EngagementManagement = () => {
    const { l } = useLocale();
    useTitle(l('_management'));
    const { selectTenant } = useTenant();
    const showGenericErrorToast = useGenericErrorToast();

    interface UserWithRole {
        id: number;
        email: string;
        role?: string;
    }

    const [availableUsers, setAvailableUsers] = useState<UserWithRole[]>([
        { id: 1, email: 'Tabdul.kadir@uk.pwc.com' },
        { id: 2, email: 'mshehab@uk.pwc.com' },
        { id: 3, email: 'jdoe@uk.pwc.com' },
    ]);

    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [searchQuery, setSearchQuery] = useState('');
    const [engagementSearch, setEngagementSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);

    const handleUserRoleChange = useCallback((userId: number, role: string) => {
        setAvailableUsers((prev) =>
            prev.map((user) => (user.id === userId ? { ...user, role } : user))
        );
    }, []);

    const filteredUsers = availableUsers.filter((user) =>
        user.email.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const [isNewEnagementModalVisible, setIsNewEngagementModalVisible] =
        useState<boolean>(false);
    const [isEditEngagementModalVisible, setIsEditEngagementModalVisible] =
        useState<boolean>(false);
    const [isDeleteEngagementModalVisible, setIsDeleteEngagementModalVisible] =
        useState<boolean>(false);
    const [isShareEngagementModalVisible, setIsShareEngagementModalVisible] =
        useState<boolean>(false);

    const [newFormData, setNewFormData] = useState<EngagementFormData>({
        engagementName: '',
        clientName: '',
        wbsCode: '',
        description: '',
    });

    const [editFormData, setEditFormData] = useState<EngagementFormData>({
        engagementName: '',
        clientName: '',
        wbsCode: '',
        description: '',
    });

    const { mutate: activateTenant, isPending: isActivating } =
        useActivateTenant({
            mutation: {
                onSuccess: (data) => {
                    selectTenant({ tenantId: data.tenantId, name: data.name });
                    navigate(`/fixedasset/${data.tenantId}`);
                },
                onError: (error) => {
                    showGenericErrorToast(error);
                },
            },
        });

    const handleGoToEngagement = useCallback(
        (engagementId: number) => {
            activateTenant({
                tenantId: engagementId,
                data: { roleKey: 'CREATOR' },
            });
        },
        [activateTenant]
    );

    const [selectedEngagementId, setSelectedEngagementId] = useState<
        number | null
    >(null);

    const { data: engagements, isLoading: loadingEngagements } =
        useGetAvailableTenants();

    const { mutate: createEngagement, isPending: isCreating } = useCreateTenant(
        {
            mutation: {
                onSuccess: () => {
                    queryClient.invalidateQueries({
                        queryKey: getGetAvailableTenantsQueryKey(),
                    });
                    handleCloseNewEngagementModal();
                },
            },
        }
    );

    const { mutate: editEngagement, isPending: isEditing } = useUpdateTenant({
        mutation: {
            onSuccess: () => {
                queryClient.invalidateQueries({
                    queryKey: getGetAvailableTenantsQueryKey(),
                });
                handleCloseEditEngagementModal();
            },
        },
    });

    const { mutate: deleteEngagement, isPending: isDeleting } = useDeleteTenant(
        {
            mutation: {
                onSuccess: () => {
                    queryClient.invalidateQueries({
                        queryKey: getGetAvailableTenantsQueryKey(),
                    });
                    handleCloseDeleteEngagementModal();
                },
            },
        }
    );

    const handleOpenNewEngagementModal = () => {
        setIsNewEngagementModalVisible(true);
    };

    const handleCloseNewEngagementModal = () => {
        setIsNewEngagementModalVisible(false);
    };

    const handleOpenDeleteEngagementModal = useCallback(
        (engagementID: number) => {
            setSelectedEngagementId(engagementID);
            setIsDeleteEngagementModalVisible(true);
        },
        []
    );

    const handleCloseDeleteEngagementModal = () => {
        setIsDeleteEngagementModalVisible(false);
    };

    const handleOpenShareEngagementModal = useCallback(
        (engagementID: number) => {
            setSelectedEngagementId(engagementID);
            setIsShareEngagementModalVisible(true);
        },
        []
    );

    const handleCloseShareEngagementModal = () => {
        setIsShareEngagementModalVisible(false);
        setSelectedEngagementId(null);
        setSearchQuery('');
    };

    const handleOpenEditEngagementModal = useCallback((engagement: any) => {
        setSelectedEngagementId(engagement.id);
        setEditFormData({
            engagementName: engagement.engName,
            clientName: engagement.client,
            wbsCode: engagement.wbsCode,
            description: engagement.description,
        });
        setIsEditEngagementModalVisible(true);
    }, []);

    const handleCloseEditEngagementModal = () => {
        setIsEditEngagementModalVisible(false);
    };

    const handleNewEngagementFormChange = useCallback(
        (field: keyof EngagementFormData, value: any) => {
            setNewFormData((prev) => ({
                ...prev,
                [field]: value,
            }));
        },
        []
    );

    const handleEditEngagementFormChange = useCallback(
        (field: keyof EngagementFormData, value: any) => {
            setEditFormData((prev) => ({
                ...prev,
                [field]: value,
            }));
        },
        []
    );

    const handleNewEngagementFormSubmit = () => {
        if (
            !newFormData.engagementName ||
            !newFormData.clientName ||
            !newFormData.wbsCode
        ) {
            alert('Please fill in all required fields');
            return;
        }

        createEngagement({
            data: {
                name: newFormData.clientName,
                engName: newFormData.engagementName,
                wbsCode: newFormData.wbsCode,
                engDescription: newFormData.description || null,
            },
        });
    };

    const handleEditEngagementFormSubmit = () => {
        if (
            !editFormData.engagementName ||
            !editFormData.clientName ||
            !editFormData.wbsCode
        ) {
            alert('Please fill in all required fields');
            return;
        }

        if (selectedEngagementId === null) return;

        editEngagement({
            tenantId: selectedEngagementId,
            data: {
                name: editFormData.clientName,
                engName: editFormData.engagementName,
                wbsCode: editFormData.wbsCode,
                engDescription: editFormData.description || null,
            },
        });
    };

    const handleDeleteEngagementButton = () => {
        if (selectedEngagementId === null) {
            alert('No engagement selected for deletion');
            return;
        }

        deleteEngagement({ tenantId: selectedEngagementId });
    };

    const handleUserSelectedButton = () => {
        if (!selectedEngagementId) {
            alert('No engagement selected');
            return;
        }

        const usersToAssign = availableUsers.filter((user) => user.role);

        if (usersToAssign.length === 0) {
            alert('Please assign at least one role to proceed');
            return;
        }
        console.log(usersToAssign);
        // TODO: API call with selectedEngagementId and usersToAssign

        setAvailableUsers((prev) =>
            prev.map((user) => ({ ...user, role: undefined }))
        );

        setSearchQuery('');
        handleCloseShareEngagementModal();
    };

    useEffect(() => {
        const outletElement = document.querySelector('.outlet-container');

        if (outletElement) {
            outletElement.classList.add('home-main');
        }

        return () => {
            if (outletElement) {
                outletElement.classList.remove('home-main');
            }
        };
    }, []);

    const roleOptions = [
        { value: 'editor', label: 'Editor' },
        { value: 'viewer', label: 'Viewer' },
        { value: 'admin', label: 'Admin' },
    ];

    const tdata = (engagements ?? []).map((eng) => ({
        id: eng.tenantId,
        engName: eng.engName ?? eng.name,
        client: eng.name,
        wbsCode: eng.wbsCode ?? '',
        description: eng.engDescription ?? '',
        action: (
            <EngagementTableIcons
                onEdit={() =>
                    handleOpenEditEngagementModal({
                        id: eng.tenantId,
                        engName: eng.engName ?? eng.name,
                        client: eng.name,
                        wbsCode: eng.wbsCode ?? '',
                        description: eng.engDescription ?? '',
                    })
                }
                onDelete={() => handleOpenDeleteEngagementModal(eng.tenantId)}
                onShare={() => handleOpenShareEngagementModal(eng.tenantId)}
                onGo={() => handleGoToEngagement(eng.tenantId)}
            />
        ),
    }));

    const filteredEngagements = tdata.filter((eng) => {
        if (!engagementSearch) return true;
        const term = engagementSearch.toLowerCase();
        return (
            eng.engName.toLowerCase().includes(term) ||
            eng.client.toLowerCase().includes(term) ||
            eng.wbsCode.toLowerCase().includes(term) ||
            eng.description.toLowerCase().includes(term)
        );
    });

    const paginatedData = filteredEngagements.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize
    );

    const engagementColumns: ColumnDef<any, any>[] = useMemo(
        () => [
            {
                accessorKey: 'engName',
                header: 'Engagement Name',
                enableSorting: true,
            },
            { accessorKey: 'client', header: 'Client', enableSorting: true },
            {
                accessorKey: 'wbsCode',
                header: 'WBS Code',
                enableSorting: true,
            },
            {
                accessorKey: 'description',
                header: 'Description',
                enableSorting: true,
            },
            {
                accessorKey: 'action',
                header: '',
                cell: ({ getValue }) => getValue(),
                enableSorting: false,
                enableColumnFilter: false,
            },
        ],
        []
    );

    const usersColumns: ColumnDef<any, any>[] = useMemo(
        () => [
            { accessorKey: 'userEmail', header: 'User' },
            {
                accessorKey: 'chooseRole',
                header: 'Choose Role',
                cell: ({ getValue }) => getValue(),
            },
        ],
        []
    );

    const tUsersData = filteredUsers.map((user) => ({
        id: user.id,
        userEmail: user.email,
        chooseRole: (
            <DropDown
                data={roleOptions}
                placeholder="Choose Role"
                value={user.role}
                onSelect={(value) => {
                    if (typeof value === 'string') {
                        handleUserRoleChange(user.id, value);
                    }
                }}
            />
        ),
    }));

    return (
        <div className="engagementPage">
            <div className="mediumNav">
                <Search
                    className="searchEngagement"
                    searchValue={engagementSearch}
                    onChange={(value: string) => {
                        setEngagementSearch(value);
                        setCurrentPage(1);
                    }}
                />
                <Button
                    onClick={handleOpenNewEngagementModal}
                    kind="primary"
                    icon="icon-plus-outline"
                >
                    New Engagement
                </Button>
            </div>
            <div className="engagementTable" data-testid="engagements-table">
                {loadingEngagements ? (
                    <Loading loadingType="circular" indeterminate />
                ) : (
                    <DataTable
                        data={paginatedData}
                        columns={engagementColumns}
                        sorting
                        columnFiltering
                        rowSelection
                        getRowId={(row) => String(row.id)}
                        resetSelectionKey={currentPage}
                    />
                )}
            </div>
            <div className="pagination">
                <Pagination
                    totalItems={filteredEngagements.length}
                    currentPage={currentPage}
                    pageSize={pageSize}
                    onPageChange={(page) => setCurrentPage(page)}
                    onPageSizeChange={(size) => {
                        setPageSize(size);
                        setCurrentPage(1);
                    }}
                />
            </div>

            <Modal
                visible={isNewEnagementModalVisible}
                onClose={handleCloseNewEngagementModal}
                title="New Engagement"
                modalStyle={{ width: '33.75rem' }}
            >
                <div className="engagementModal">
                    {isCreating ? (
                        <Loading loadingType="circular" indeterminate />
                    ) : (
                        <>
                            <EngagementForm
                                formData={newFormData}
                                onChange={handleNewEngagementFormChange}
                            />
                            <Button
                                onClick={handleNewEngagementFormSubmit}
                                kind="primary"
                                disabled={
                                    !newFormData.engagementName ||
                                    !newFormData.clientName ||
                                    !newFormData.wbsCode
                                }
                            >
                                Create Engagement
                            </Button>
                        </>
                    )}
                </div>
            </Modal>

            <Modal
                visible={isEditEngagementModalVisible}
                onClose={handleCloseEditEngagementModal}
                title="Edit Engagement"
                modalStyle={{ width: '33.75rem' }}
            >
                <div className="engagementModal">
                    {isEditing ? (
                        <Loading loadingType="circular" indeterminate />
                    ) : (
                        <>
                            <EngagementForm
                                formData={editFormData}
                                onChange={handleEditEngagementFormChange}
                            />
                            <Button
                                onClick={handleEditEngagementFormSubmit}
                                kind="primary"
                                disabled={
                                    !editFormData.engagementName ||
                                    !editFormData.clientName ||
                                    !editFormData.wbsCode
                                }
                            >
                                Save Changes
                            </Button>
                        </>
                    )}
                </div>
            </Modal>

            <Modal
                visible={isDeleteEngagementModalVisible}
                onClose={handleCloseDeleteEngagementModal}
                title="Delete Engagement?"
            >
                <div className="engagementModal">
                    {isDeleting ? (
                        <Loading loadingType="circular" indeterminate />
                    ) : (
                        <>
                            <p>
                                Are you sure you want to delete this engagement?
                                This action is permanent.
                            </p>
                            <div className="deleteButtons">
                                <Button
                                    onClick={handleCloseDeleteEngagementModal}
                                    kind="secondary"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleDeleteEngagementButton}
                                    kind="primary"
                                >
                                    Delete
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </Modal>

            <Modal
                visible={isShareEngagementModalVisible}
                onClose={handleCloseShareEngagementModal}
                title="Assign Users"
            >
                <div className="engagementUsersModal">
                    <Search
                        placeholder="Search Users"
                        value={searchQuery}
                        onChange={(value) => setSearchQuery(value)}
                    />
                    <div className="engagementUsersTable">
                        <DataTable
                            data={tUsersData}
                            columns={usersColumns}
                            striped
                        />
                    </div>
                    <Button onClick={handleUserSelectedButton} kind="primary">
                        Save Changes
                    </Button>
                    <div className="engagementUsersPagination">
                        <Pagination />
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default EngagementManagement;
